
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { reviews } from '../../../mocks/reviews';

const INITIAL_SHOW = 6;

export default function ComparisonTable() {
  const [showAll, setShowAll] = useState(false);
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 });

  // Sort by date descending (most recent first), fallback to original order
  const sorted = [...reviews].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });

  const displayed = showAll ? sorted : sorted.slice(0, INITIAL_SHOW);

  const renderStars = (rating: number, index: number, tableVisible: boolean) => (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <i
          key={i}
          className={`ri-star-fill text-lg transition-all duration-300 group-hover:scale-110 ${
            i < Math.round(rating) ? 'text-[#D4AF37]' : 'text-gray-200'
          }`}
          style={{ transitionDelay: `${i * 40}ms` }}
        />
      ))}
      <span className="ml-1.5 text-sm font-semibold text-gray-500">{rating.toFixed(1)}</span>
    </div>
  );

  return (
    <section className="py-20 bg-gradient-to-b from-[#FFF9F5] to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={titleRef}
          className={`text-center mb-12 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1B4332] mb-4">
            Quick Gift Comparison
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Compare our top-rated gifts side by side to find the perfect match
          </p>
        </div>

        <div
          ref={tableRef}
          className={`bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-700 ${
            tableVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#8B2635] to-[#A52A3A]">
                  <th className="px-6 py-5 text-left text-white font-bold text-base">Product</th>
                  <th className="px-6 py-5 text-left text-white font-bold text-base">Category</th>
                  <th className="px-6 py-5 text-left text-white font-bold text-base">Best For</th>
                  <th className="px-6 py-5 text-left text-white font-bold text-base">Our Rating</th>
                  <th className="px-6 py-5 text-center text-white font-bold text-base">Read Review</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((product, index) => (
                  <tr
                    key={product.id}
                    className={`border-b border-gray-100 hover:bg-[#D4AF37]/5 transition-all duration-300 cursor-pointer group ${
                      index % 2 === 0 ? 'bg-white' : 'bg-[#FFF9F5]'
                    } ${tableVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${index * 80}ms` }}
                  >
                    {/* Product */}
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-xl bg-gray-50 group-hover:shadow-lg transition-shadow duration-300">
                          <img
                            src={product.heroImage || product.image}
                            alt={product.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h4 className="font-bold text-[#1B4332] text-sm leading-snug max-w-[180px] group-hover:text-[#8B2635] transition-colors">
                          {product.name}
                        </h4>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-6 py-5">
                      <span className="inline-block px-3 py-1.5 bg-[#1B4332]/5 text-[#1B4332] text-xs font-semibold rounded-lg whitespace-nowrap group-hover:bg-[#1B4332]/10 transition-colors">
                        {product.category}
                      </span>
                    </td>

                    {/* Best For */}
                    <td className="px-6 py-5 max-w-[220px]">
                      <p className="text-sm text-gray-600 leading-snug line-clamp-2">
                        {product.bestFor}
                      </p>
                    </td>

                    {/* Rating */}
                    <td className="px-6 py-5">
                      {renderStars(product.rating, index, tableVisible)}
                    </td>

                    {/* CTA */}
                    <td className="px-6 py-5 text-center">
                      <Link
                        to={`/reviews/${product.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#8B2635] to-[#A52A3A] text-white rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-[#8B2635]/30 transition-all duration-300 hover:scale-105 active:scale-95 whitespace-nowrap"
                      >
                        Read Review
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!showAll && sorted.length > INITIAL_SHOW && (
            <div className="p-8 text-center border-t border-gray-100">
              <button
                onClick={() => setShowAll(true)}
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#D4AF37] text-[#D4AF37] rounded-full font-semibold hover:bg-[#D4AF37] hover:text-white transition-all duration-300 whitespace-nowrap group"
              >
                Show All {sorted.length} Products
                <i className="ri-arrow-down-line group-hover:translate-y-1 transition-transform" />
              </button>
            </div>
          )}
        </div>

        <div
          className={`mt-8 p-6 bg-[#D4AF37]/10 rounded-2xl border border-[#D4AF37]/20 transition-all duration-700 delay-300 ${
            tableVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37] rounded-full flex-shrink-0">
              <i className="ri-information-fill text-2xl text-white" />
            </div>
            <div>
              <h4 className="font-bold text-[#1B4332] mb-2">Amazon Affiliate Disclosure</h4>
              <p className="text-gray-700 leading-relaxed">
                Best Christmas Gift Reviews is a participant in the Amazon Services LLC Associates Program, an affiliate advertising program designed to provide a means for sites to earn advertising fees by advertising and linking to Amazon.com. We may earn a small commission from qualifying purchases at no additional cost to you. This helps us continue providing honest, unbiased reviews.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
